import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditSensingStationComposer } from "@/forms/sensnet/sensingStations"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { useToast } from "@/contexts/ToastContext"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { RadioTower, Loader2 } from "lucide-react"
import type { GetNetworkSitesResponse } from "@/types/sensNet"

function EditSensingStationPage() {
  const { stationNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useApiQuery("getStationByNumber", { stationNumber })
  const { data: sitesData } = useApiQuery<GetNetworkSitesResponse>("getNetworkSites", {})

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = (data as any)?.content?.station
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allSites: any[] = (sitesData as any)?.content?.networkSites || (sitesData as any)?.networkSites || []

  const handleBack = () => navigate({ to: '/sensnet/sensing-stations' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const coveredBySites = s?.coveredBySites || []
  const firstSiteId = coveredBySites?.[0]
  const corridorId = (() => {
    if (!firstSiteId) return undefined
    const site = allSites.find((x) => String(x.networkSiteNumber) === String(firstSiteId))
    return site?.networkCorridorNumber ? String(site.networkCorridorNumber) : undefined
  })()

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <RadioTower className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Sensing Station</CardTitle>
              <CardDescription className="text-base">Update sensing station details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {s ? (
            <EditSensingStationComposer
              // Data-versioned key: re-mount when a fresh copy arrives so the form
              // re-initializes instead of showing previously-cached values.
              key={`station-${s.stationNumber}-${s.updatedAt ?? ''}`}
              initialValues={{
                stationNumber: s.stationNumber,
                corridorId,
                name: s.stationName || s.name || '',
                geoLocation: s.location || s.geoLocation || { latitude: 0, longitude: 0 },
                addressIfAvailable: s.stationAddress || s.addressIfAvailable || '',
                stationRoadIntersections: s.stationRoadIntersections || '',
                stationNotes: s.stationNotes || '',
                coveredBySites: coveredBySites,
                stationPictures: (s.stationPictures || []) as unknown as File[],
              }}
              submitLabel="Save Station"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update station: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Sensing station not found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/sensnet/sensing-stations/$stationNumber_/edit")({
  staticData: {
    title: 'Edit Sensing Station',
    isSecured: true,
  },
  component: EditSensingStationPage,
})
